"use client";

import { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Fade,
  Grid,
} from "@mui/material";
import {
  CloudUpload,
  ContentCopy,
  Delete,
  Download,
  ExpandMore,
  CheckCircle,
  Code,
  Tune,
} from "@mui/icons-material";

function byteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(2)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}

const SAMPLE_JS = `// Example: Minify this JavaScript
function greet(name) {
  const message = "Hello, " + name + "!";
  console.log(message);
  return message;
}

greet("World");
`;

export default function JsMinifierConverter() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [minifying, setMinifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  const [compress, setCompress] = useState(true);
  const [mangle, setMangle] = useState(true);
  const [dropConsole, setDropConsole] = useState(false);
  const [keepLicenseComments, setKeepLicenseComments] = useState(true);

  const inputBytes = useMemo(() => byteLength(input), [input]);
  const outputBytes = useMemo(() => byteLength(output), [output]);
  const savingsPct = useMemo(() => {
    if (!output || inputBytes === 0) return null;
    const pct = ((inputBytes - outputBytes) / inputBytes) * 100;
    if (!Number.isFinite(pct)) return null;
    return Math.max(0, Math.min(100, pct));
  }, [inputBytes, outputBytes, output]);

  const handleMinify = async () => {
    setError(null);
    setCopied(false);

    const code = input.trim();
    if (!code) {
      setError("Please paste your JavaScript code to minify.");
      setOutput("");
      return;
    }

    setMinifying(true);
    try {
      const terser = await import("terser");
      const result = await terser.minify(code, {
        compress: compress
          ? {
              passes: 2,
              drop_console: dropConsole,
            }
          : false,
        mangle: mangle,
        format: {
          comments: keepLicenseComments ? /@license|@preserve|^!/ : false,
        },
      });

      if (result.code && typeof result.code === "string") {
        setOutput(result.code);
      } else {
        setOutput("");
        setError("Minification finished, but no output was generated.");
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to minify JavaScript. Please check your input.";
      setError(message);
      setOutput("");
    } finally {
      setMinifying(false);
    }
  };

  const handleCopy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Failed to copy to clipboard");
    }
  };

  const handleDownload = () => {
    if (!output) return;
    const blob = new Blob([output], { type: "application/javascript" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "script.min.js";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClear = () => {
    setInput("");
    setOutput("");
    setError(null);
    setCopied(false);
  };

  const handleLoadSample = () => {
    setInput(SAMPLE_JS);
    setOutput("");
    setError(null);
    setCopied(false);
  };

  const handlePickFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (file: File | null) => {
    if (!file) return;
    try {
      const text = await file.text();
      setInput(text);
      setOutput("");
      setError(null);
      setCopied(false);
    } catch {
      setError("Failed to read the selected file.");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            JavaScript input
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <input
              ref={fileInputRef}
              type="file"
              accept=".js,.mjs,.cjs,application/javascript,text/javascript"
              style={{ display: "none" }}
              onChange={(e) => handleFileSelected(e.target.files?.[0] ?? null)}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={handlePickFile}
            >
              Upload .js
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Code />}
              onClick={handleLoadSample}
            >
              Load sample
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Tune />}
              onClick={() => setShowOptions((v) => !v)}
              endIcon={
                <ExpandMore
                  sx={{
                    transition: "transform 0.2s ease",
                    transform: showOptions ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              }
            >
              Options
            </Button>
          </Stack>
        </Stack>

        <TextField
          multiline
          rows={10}
          fullWidth
          placeholder={`Paste JavaScript here...\n\nTip: You can also upload a .js file.`}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
            if (output) setOutput("");
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontFamily: "monospace",
              fontSize: "0.9rem",
            },
          }}
        />

        <Collapse in={showOptions}>
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Minification options
            </Typography>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={compress}
                      onChange={(e) => setCompress(e.target.checked)}
                    />
                  }
                  label="Compress (recommended)"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={mangle}
                      onChange={(e) => setMangle(e.target.checked)}
                    />
                  }
                  label="Mangle names (smaller output)"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dropConsole}
                      onChange={(e) => setDropConsole(e.target.checked)}
                      disabled={!compress}
                    />
                  }
                  label="Drop console.* (when compressing)"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={keepLicenseComments}
                      onChange={(e) => setKeepLicenseComments(e.target.checked)}
                    />
                  }
                  label="Keep license comments (/*! ... */)"
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 3 }}
          flexWrap="wrap"
        >
          <Button
            variant="contained"
            size="large"
            startIcon={
              minifying ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                <Code sx={{ color: "#ffffff" }} />
              )
            }
            onClick={handleMinify}
            disabled={!input.trim() || minifying}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: "1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {minifying ? "Minifying..." : "Minify JavaScript"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Delete />}
            onClick={handleClear}
          >
            Clear
          </Button>
        </Stack>

        {/* Output */}
        {output && (
          <Fade in={!!output}>
            <Box sx={{ mt: 4 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                sx={{ mb: 2 }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700}>
                    Minified output
                  </Typography>
                  <Chip
                    size="small"
                    label={`Input: ${formatBytes(inputBytes)}`}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={`Output: ${formatBytes(outputBytes)}`}
                    color="success"
                    variant="outlined"
                  />
                  {savingsPct !== null && (
                    <Chip
                      size="small"
                      label={`Saved: ${savingsPct.toFixed(1)}%`}
                      color="success"
                    />
                  )}
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                    onClick={handleCopy}
                    variant="outlined"
                    color={copied ? "success" : "primary"}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Download />}
                    onClick={handleDownload}
                    variant="outlined"
                  >
                    Download .min.js
                  </Button>
                </Stack>
              </Stack>

              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  bgcolor: "#1e1e1e",
                  borderRadius: 2,
                  maxHeight: 320,
                  overflow: "auto",
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                    color: "#d4d4d4",
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {output}
                </Typography>
              </Paper>
            </Box>
          </Fade>
        )}
      </Paper>
    </Box>
  );
}
